import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type User, usersProvider } from "@/providers/users.provider";
import {
  type Vehicle,
  vehiclesProvider,
} from "@/providers/vehicles.provider";
import {
  IconCar,
  IconChevronLeft,
  IconChevronRight,
  IconMotorbike,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

function VehicleTypeBadge({ type }: { type: Vehicle["vehicle_type"] }) {
  if (type === "motorcycle")
    return (
      <Badge variant="secondary" className="gap-1">
        <IconMotorbike className="size-3" />
        Motorcycle
      </Badge>
    );
  return (
    <Badge variant="outline" className="gap-1">
      <IconCar className="size-3" />
      Car
    </Badge>
  );
}

function VehicleRow({
  vehicle,
  onClick,
}: {
  vehicle: Vehicle;
  onClick: () => void;
}) {
  const createdAt = new Date(vehicle.created_at).toLocaleDateString();
  const displayName = vehicle.nickname
    ? `${vehicle.make} ${vehicle.model} "${vehicle.nickname}"`
    : `${vehicle.make} ${vehicle.model}`;

  return (
    <TableRow className="cursor-pointer" onClick={onClick}>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {vehicle.vehicle_id}
      </TableCell>
      <TableCell className="font-medium">{displayName}</TableCell>
      <TableCell className="tabular-nums">{vehicle.year}</TableCell>
      <TableCell>
        <VehicleTypeBadge type={vehicle.vehicle_type} />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {vehicle.drive ?? "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {vehicle.transmission === "A"
          ? "Automatic"
          : vehicle.transmission === "M"
            ? "Manual"
            : "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {vehicle.horsepower != null ? `${vehicle.horsepower} hp` : "—"}
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">
        {createdAt}
      </TableCell>
    </TableRow>
  );
}

function CreateVehicleSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [vehicleType, setVehicleType] = useState<"car" | "motorcycle">("car");
  const [nickname, setNickname] = useState("");
  const [modList, setModList] = useState("");
  const [horsepower, setHorsepower] = useState("");
  const [drive, setDrive] = useState<"FWD" | "RWD" | "AWD" | "">("");
  const [transmission, setTransmission] = useState<"A" | "M" | "">("");
  const [enginePosition, setEnginePosition] = useState<
    "front" | "center" | "rear" | ""
  >("");
  const [imageFile, setImageFile] = useState<{
    file: File;
    base64: string;
  } | null>(null);

  function reset() {
    setMake("");
    setModel("");
    setYear("");
    setVehicleType("car");
    setNickname("");
    setModList("");
    setHorsepower("");
    setDrive("");
    setTransmission("");
    setEnginePosition("");
    setImageFile(null);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPEG and PNG images are supported");
      return;
    }
    if (file.size > 100 * 1024) {
      toast.error("Image must be under 100 KB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      setImageFile({ file, base64 });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const parsedYear = parseInt(year, 10);
      const parsedHp = horsepower ? parseInt(horsepower, 10) : undefined;

      return vehiclesProvider.create({
        make: make.trim(),
        model: model.trim(),
        year: parsedYear,
        photo_content: imageFile!.base64,
        vehicle_type: vehicleType,
        nickname: nickname.trim() || undefined,
        mod_list: modList.trim() || undefined,
        horsepower: parsedHp,
        drive: drive || undefined,
        transmission: transmission || undefined,
        engine_position: enginePosition || undefined,
      });
    },
    onSuccess: () => {
      toast.success("Vehicle created successfully");
      reset();
      onCreated();
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const canSubmit =
    make.trim() &&
    model.trim() &&
    year.trim() &&
    !isNaN(parseInt(year, 10)) &&
    imageFile !== null;

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle>Create Vehicle</SheetTitle>
          <SheetDescription>
            Add a new vehicle. A photo is required.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 flex flex-col gap-4 py-4">
          {/* Photo upload */}
          <div className="flex flex-col gap-1.5">
            <Label>Photo *</Label>
            <div className="flex items-center gap-3">
              <div
                className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => fileRef.current?.click()}
              >
                {imageFile ? (
                  <img
                    src={URL.createObjectURL(imageFile.file)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconPhoto className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="gap-1.5"
                >
                  <IconUpload className="size-3.5" />
                  {imageFile ? "Change photo" : "Choose photo"}
                </Button>
                {imageFile && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive text-left"
                    onClick={() => setImageFile(null)}
                  >
                    Remove
                  </button>
                )}
                <p className="text-xs text-muted-foreground">
                  JPEG or PNG, max 100 KB
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <Separator />

          {/* Basic info */}
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  placeholder="Toyota"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  placeholder="Supra"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  placeholder="1993"
                  min={1900}
                  max={new Date().getFullYear() + 1}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Type</Label>
                <Select
                  value={vehicleType}
                  onValueChange={(v) =>
                    setVehicleType(v as "car" | "motorcycle")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="motorcycle">Motorcycle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                placeholder='e.g. "MK4"'
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Specs */}
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold">Specs</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Drivetrain</Label>
                <Select
                  value={drive}
                  onValueChange={(v) => setDrive(v as typeof drive)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FWD">FWD</SelectItem>
                    <SelectItem value="RWD">RWD</SelectItem>
                    <SelectItem value="AWD">AWD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Transmission</Label>
                <Select
                  value={transmission}
                  onValueChange={(v) =>
                    setTransmission(v as typeof transmission)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Manual</SelectItem>
                    <SelectItem value="A">Automatic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Engine Position</Label>
                <Select
                  value={enginePosition}
                  onValueChange={(v) =>
                    setEnginePosition(v as typeof enginePosition)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="front">Front</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="rear">Rear</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="hp">Horsepower</Label>
                <Input
                  id="hp"
                  type="number"
                  placeholder="320"
                  min={0}
                  value={horsepower}
                  onChange={(e) => setHorsepower(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Mods */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mods">Modifications</Label>
            <Textarea
              id="mods"
              placeholder="Turbo upgrade, Coilovers, Exhaust…"
              value={modList}
              onChange={(e) => setModList(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <SheetFooter className="px-4 pb-4">
          <Button
            className="w-full"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Creating…" : "Create Vehicle"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function VehicleDetailSheet({
  vehicle,
  onClose,
}: {
  vehicle: Vehicle | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (vehicleId: string) => vehiclesProvider.delete(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles-by-user"] });
      onClose();
    },
  });

  // Reset confirm state when sheet closes or different vehicle opens
  useEffect(() => {
    if (!vehicle) setConfirmDelete(false);
  }, [vehicle]);

  const createdAt = vehicle
    ? new Date(vehicle.created_at).toLocaleDateString()
    : "";
  const updatedAt = vehicle
    ? new Date(vehicle.updated_at).toLocaleDateString()
    : "";

  return (
    <Sheet
      open={!!vehicle}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {vehicle && (
          <>
            <SheetHeader>
              <SheetTitle>
                {vehicle.nickname
                  ? `${vehicle.make} ${vehicle.model} "${vehicle.nickname}"`
                  : `${vehicle.make} ${vehicle.model}`}
              </SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {vehicle.vehicle_id}
              </SheetDescription>
            </SheetHeader>

            <div className="px-4 flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Type</span>
                <VehicleTypeBadge type={vehicle.vehicle_type} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Owner</span>
                <span className="font-medium">{vehicle.username}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">User ID</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {vehicle.user_id}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Make</span>
                <span>{vehicle.make}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Model</span>
                <span>{vehicle.model}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Year</span>
                <span className="tabular-nums">{vehicle.year}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Nickname</span>
                <span>{vehicle.nickname ?? "—"}</span>
              </div>
            </div>

            <Separator />

            <div className="px-4 flex flex-col gap-3 text-sm">
              <h3 className="font-semibold text-sm">Specs</h3>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Drivetrain</span>
                <span>{vehicle.drive ?? "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Transmission</span>
                <span>
                  {vehicle.transmission === "A"
                    ? "Automatic"
                    : vehicle.transmission === "M"
                      ? "Manual"
                      : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Engine Position</span>
                <span className="capitalize">
                  {vehicle.engine_position ?? "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Horsepower</span>
                <span className="tabular-nums">
                  {vehicle.horsepower != null
                    ? `${vehicle.horsepower} hp`
                    : "—"}
                </span>
              </div>
            </div>

            {vehicle.mod_list && (
              <>
                <Separator />
                <div className="px-4 flex flex-col gap-2 text-sm">
                  <h3 className="font-semibold text-sm">Modifications</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {vehicle.mod_list}
                  </p>
                </div>
              </>
            )}

            <Separator />

            <div className="px-4 flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{createdAt}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last updated</span>
                <span>{updatedAt}</span>
              </div>
              {vehicle.photo_url && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-muted-foreground">Photo</span>
                  <img
                    src={vehicle.photo_url}
                    alt="Vehicle photo"
                    className="w-full rounded-lg border object-cover max-h-48"
                  />
                </div>
              )}
            </div>

            <Separator />

            <div className="px-4 flex flex-col gap-2">
              <h3 className="font-semibold text-sm text-destructive">
                Danger Zone
              </h3>
              {deleteMutation.isError && (
                <p className="text-xs text-destructive">
                  {deleteMutation.error?.message ?? "Failed to delete vehicle."}
                </p>
              )}
              {confirmDelete ? (
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground">
                    This will permanently delete this vehicle. Are you sure?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1"
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(vehicle.vehicle_id)}
                    >
                      {deleteMutation.isPending ? "Deleting…" : "Yes, delete"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-1.5"
                  onClick={() => setConfirmDelete(true)}
                >
                  <IconTrash className="size-3.5" />
                  Delete Vehicle
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}


export function VehiclesPage() {
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [debouncedUserQuery, setDebouncedUserQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedUserQuery(userQuery.trim()), 300);
    return () => clearTimeout(id);
  }, [userQuery]);

  const queryClient = useQueryClient();

  const { data: userResults, isFetching: usersFetching } = useQuery({
    queryKey: ["users-search", debouncedUserQuery],
    queryFn: () => usersProvider.search(debouncedUserQuery),
    enabled: debouncedUserQuery.length >= 1,
    staleTime: 30_000,
  });

  const committedUserId = selectedUser?.UserId ?? "";

  const {
    data: vehicles,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["vehicles-by-user", committedUserId, page],
    queryFn: () => vehiclesProvider.getByUser(committedUserId),
    enabled: !!committedUserId,
  });

  const pagedVehicles = vehicles
    ? vehicles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : [];
  const hasMore = pagedVehicles.length === PAGE_SIZE;

  function handleSelectUser(user: User) {
    setSelectedUser(user);
    setUserQuery("");
    setDropdownOpen(false);
    setPage(1);
  }

  function handleClearUser() {
    setSelectedUser(null);
    setUserQuery("");
    setPage(1);
  }

  const showPrompt = !committedUserId;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Vehicles" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {selectedUser ? (
              <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 w-80">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {selectedUser.Username}
                  </p>
                  <p className="truncate text-xs text-muted-foreground font-mono">
                    {selectedUser.UserId}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-xs h-6 px-2"
                  onClick={handleClearUser}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="relative w-80">
                <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search users by name, email or username…"
                  value={userQuery}
                  autoComplete="off"
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                  className="pl-8 pr-8"
                />
                {userQuery && (
                  <button
                    type="button"
                    onClick={() => setUserQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <IconX className="size-4" />
                  </button>
                )}
                {dropdownOpen && userQuery.trim().length >= 1 && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-md">
                    {usersFetching ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        Searching…
                      </p>
                    ) : !userResults?.length ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        No users found
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        {userResults.map((u) => (
                          <button
                            key={u.UserId}
                            type="button"
                            className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectUser(u)}
                          >
                            <span className="text-sm font-medium">
                              {u.Username}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {u.Email}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-muted-foreground">
              <IconCar className="size-4" />
              <span className="text-sm">
                {isLoading
                  ? "Loading…"
                  : vehicles
                    ? `${vehicles.length} vehicle${vehicles.length !== 1 ? "s" : ""}`
                    : ""}
              </span>
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setCreateOpen(true)}
            >
              <IconPlus className="size-3.5" />
              Create Vehicle
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehicle ID</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Drive</TableHead>
                <TableHead>Transmission</TableHead>
                <TableHead>Horsepower</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {error?.message ?? "Failed to load vehicles."}
                  </TableCell>
                </TableRow>
              ) : showPrompt ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Search for a user above to load their vehicles.
                  </TableCell>
                </TableRow>
              ) : !pagedVehicles.length ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {page > 1
                      ? "No more vehicles on this page."
                      : "This user has no vehicles."}
                  </TableCell>
                </TableRow>
              ) : (
                pagedVehicles.map((vehicle) => (
                  <VehicleRow
                    key={vehicle.vehicle_id}
                    vehicle={vehicle}
                    onClick={() => setSelectedVehicle(vehicle)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {page > 1 || hasMore ? (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="gap-1"
            >
              <IconChevronLeft className="size-3.5" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Next
              <IconChevronRight className="size-3.5" />
            </Button>
          </div>
        ) : null}
      </div>

      <VehicleDetailSheet
        vehicle={selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
      />

      <CreateVehicleSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          if (committedUserId) {
            queryClient.invalidateQueries({ queryKey: ["vehicles-by-user"] });
          }
        }}
      />
    </div>
  );
}
